import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Message } from 'primereact/message'
import { Sidebar } from 'primereact/sidebar'
import { ConfirmDialog } from '../confirmDialog/ConfirmDialog'
import { messageActions } from '../../store/message'
import {
  excluirConjunto,
  executarConjunto,
  marcarPadraoConjunto,
  padraoConjunto,
  pesquisarConjuntos,
  resolverDatas,
} from '../../services/api-agendamento-relatorio'
import { AgendarRelatorioDialog } from './AgendarRelatorioDialog'
import { abrirAbaEmBranco, fecharAba, lerFiltros, mensagemDeErro, mostrarNaAba } from './agendamentoRelatorio.utils'
import { firstDaUltimaPagina, formatarTexto, visivelPara } from './filtrosSalvos.utils'
import { comDatasResolvidas, formulasDoPadrao } from './padraoNaTela'
import { textoAgendamento, textosAgendamento } from './textos'
import './FiltrosSalvosRelatorio.scss'

const LINHAS = 10

const mapDispatchToProps = (dispatch) => ({
  // fora do painel (a tela acabou de abrir): o aviso da pagina aparece normalmente
  mostrarAviso: (mensagem) => dispatch(messageActions.showMessages([mensagem], 'warn')),
})

/**
 * Botoes "Salvar" e "Filtros salvos" das telas de relatorio (PHOENIX-107), ao lado do Agendar. Para todos que
 * acessam o relatorio (sem permissao propria). O filtro salvo e um agendamento sem envio: o dialogo e o do Agendar,
 * em modo conjunto.
 *
 * Props:
 * - relatorio: { path, oidDocumento, descricao }, como no AgendarRelatorioDialog
 * - camposData, EditorFiltros: como no AgendarRelatorioDialog
 * - filtrosDaTela(): os filtros atuais da tela, lidos no clique do Salvar e do Novo
 * - aplicarPadrao(filtros, vigente) (opcional, fase 3): preenche a tela com o filtro padrao do usuario, que chega com
 *   as datas ja calculadas para hoje (yyyy-MM-dd); devolve (ou promete) os nomes dos itens que nao existem mais e
 *   ficaram de fora, null se nao aplicou, ou { naoAplicado, descartados } quando nenhuma unidade do padrao esta mais
 *   disponivel (ver aplicarPreparado). Antes de mexer na tela confere vigente(): falso = a tela saiu ou o usuario
 *   ja mudou algum filtro enquanto o padrao carregava, e entao nao aplica. Sem ele a tela nao abre com o padrao
 */
const FiltrosSalvosRelatorioBase = ({
  relatorio,
  camposData,
  EditorFiltros,
  filtrosDaTela,
  aplicarPadrao,
  mostrarAviso,
}) => {
  const textos = useMemo(() => textosAgendamento(), [])
  const msg = (chave) => textoAgendamento(chave, textos)

  const [painelVisivel, setPainelVisivel] = useState(false)
  // dialogo: null = fechado; { conjunto } = edicao; { filtrosTela } = novo
  const [dialogo, setDialogo] = useState(null)
  const [pagina, setPaginaState] = useState({ first: 0, rows: LINHAS })
  const [conjuntos, setConjuntos] = useState([])
  const [total, setTotal] = useState(0)
  const [carregando, setCarregando] = useState(false)
  const [erro, setErro] = useState(null)
  // dentro do painel: a mensagem da pagina (messageActions) fica por baixo da mascara da Sidebar
  const [sucesso, setSucesso] = useState(null)
  const [aExcluir, setAExcluir] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  // oids dos filtros com relatorio rodando: so o botao da linha fica travado, os outros seguem livres
  const [aplicando, setAplicando] = useState({})
  const montado = useRef(true)
  const ultimaPesquisa = useRef(0)
  // a pagina atual, para a recarga depois de Padrao/Excluir: a do clique pode ja ter sido trocada no paginador
  const paginaAtual = useRef(pagina)
  const setPagina = (nova) => {
    paginaAtual.current = nova
    setPaginaState(nova)
  }

  useEffect(() => {
    montado.current = true
    return () => {
      montado.current = false
    }
  }, [])

  // filtro padrao (P2): a tela abre preenchida com ele, sem executar; o nome fica ao lado dos botoes ate sair da tela
  const [padraoAplicado, setPadraoAplicado] = useState(null)
  // os filtros da tela de agora (a funcao muda a cada render da tela), para saber se o usuario mexeu antes do padrao
  const filtrosDaTelaAtual = useRef(filtrosDaTela)
  filtrosDaTelaAtual.current = filtrosDaTela
  const fotoDaTela = () => {
    try {
      return JSON.stringify(filtrosDaTelaAtual.current())
    } catch (e) {
      return null
    }
  }
  const chaveRelatorio = `${relatorio.path}|${relatorio.oidDocumento || ''}`
  useEffect(() => {
    if (!aplicarPadrao || !relatorio.path) {
      return undefined
    }
    let vale = true
    setPadraoAplicado(null)
    padraoConjunto(relatorio)
      .then((resposta) => {
        const conjunto = resposta.data
        if (!vale || !conjunto || !conjunto.oid) {
          return null
        }
        // a tela como estava quando o padrao chegou (as cargas da montagem, como a unidade do topo, ja passaram): se
        // mudar ate a hora de aplicar, foi o usuario, e o padrao nao passa por cima
        const foto = fotoDaTela()
        const vigente = () => vale && fotoDaTela() === foto
        const filtros = lerFiltros(conjunto.filtros)
        const formulas = formulasDoPadrao(filtros, camposData)
        // formula que nao resolveu: a data fica como a tela abriu
        const datas = Object.keys(formulas).length
          ? resolverDatas(formulas)
              .then((r) => r.data || {})
              .catch(() => ({}))
          : Promise.resolve({})
        return datas
          .then((resolvidas) =>
            vigente() ? aplicarPadrao(comDatasResolvidas(filtros, camposData, resolvidas), vigente) : null
          )
          .then((descartados) => {
            if (vale && descartados && descartados.naoAplicado) {
              const modelo = msg('conjunto.padrao.naoAplicado')
              mostrarAviso(formatarTexto(modelo, conjunto.descricao, descartados.descartados.join(', ')))
              return
            }
            if (!vale || !Array.isArray(descartados)) {
              return
            }
            setPadraoAplicado(conjunto.descricao)
            if (descartados.length > 0) {
              const modelo = msg('conjunto.padrao.descartados')
              mostrarAviso(formatarTexto(modelo, conjunto.descricao, descartados.join(', ')))
            }
          })
      })
      // sem o padrao (falha de rede, sessao) a tela abre como sempre abriu
      .catch(() => {})
    return () => {
      vale = false
    }
  }, [chaveRelatorio]) // eslint-disable-line react-hooks/exhaustive-deps

  const pesquisar = (first = paginaAtual.current.first, rows = paginaAtual.current.rows) => {
    const pedido = ++ultimaPesquisa.current
    setCarregando(true)
    return pesquisarConjuntos({
      path: relatorio.path,
      oidDocumento: relatorio.oidDocumento || null,
      index: first,
      max: rows,
    })
      .then((resposta) => {
        if (!montado.current || pedido !== ultimaPesquisa.current) {
          return
        }
        const dados = resposta.data || {}
        const registros = dados.records || []
        // pagina alem do fim (ex. excluido o unico item da ultima): pede a ultima que ainda tem itens
        const anterior = registros.length ? null : firstDaUltimaPagina(first, rows, dados.count || 0)
        if (anterior !== null) {
          setPagina({ first: anterior, rows })
          return pesquisar(anterior, rows)
        }
        setConjuntos(registros)
        setTotal(dados.count || 0)
      })
      .catch((e) => montado.current && setErro(mensagemDeErro(e, msg('erro.operacao'))))
      .finally(() => montado.current && pedido === ultimaPesquisa.current && setCarregando(false))
  }

  const abrirPainel = (mensagem = null) => {
    setErro(null)
    setSucesso(mensagem)
    setPainelVisivel(true)
    setPagina({ ...paginaAtual.current, first: 0 })
    pesquisar(0, paginaAtual.current.rows)
  }

  const novo = () => setDialogo({ filtrosTela: filtrosDaTela() })

  // depois de salvar: volta para a lista, com o recem-gravado nela. Novo vai para a primeira pagina; a edicao fica na
  // pagina em que estava, onde o editado continua
  const aoSalvar = () => {
    if (!dialogo || !dialogo.conjunto) {
      abrirPainel(msg('conjunto.salvo'))
      return
    }
    setErro(null)
    setSucesso(msg('conjunto.salvo'))
    setPainelVisivel(true)
    pesquisar()
  }

  // Padrao, Editar e Excluir ficam travados ate a lista voltar: a linha mostra o valor antigo ate la (o Editar abriria
  // com o padrao antigo e o desfaria ao salvar), e um segundo clique repetiria o mesmo pedido em vez de desfazer
  const executarNaLista = (chamada, depois) => {
    if (ocupado) {
      return
    }
    setErro(null)
    setSucesso(null)
    setOcupado(true)
    chamada()
      .then(depois)
      .catch((e) => montado.current && setErro(mensagemDeErro(e, msg('erro.operacao'))))
      .finally(() => montado.current && setOcupado(false))
  }

  // um relatorio por filtro: o clique duplo abriria duas abas e rodaria o relatorio duas vezes no back
  const aplicar = (conjunto) => {
    if (aplicando[conjunto.oid]) {
      return
    }
    // a aba abre no clique: aberta so na resposta do back, o navegador a trata como pop-up e bloqueia
    const janela = abrirAbaEmBranco()
    setErro(null)
    setSucesso(null)
    setAplicando((atual) => ({ ...atual, [conjunto.oid]: true }))
    executarConjunto(conjunto.oid)
      .then((resposta) => mostrarNaAba(janela, resposta.data))
      .catch((e) => {
        fecharAba(janela)
        if (montado.current) {
          setErro(mensagemDeErro(e, msg('erro.operacao')))
        }
      })
      .finally(() => {
        if (montado.current) {
          setAplicando((atual) => {
            const resto = { ...atual }
            delete resto[conjunto.oid]
            return resto
          })
        }
      })
  }

  const alternarPadrao = (conjunto) =>
    executarNaLista(
      () => marcarPadraoConjunto(conjunto.oid, !conjunto.padrao),
      () => pesquisar()
    )

  const excluir = (conjunto) =>
    executarNaLista(
      () => excluirConjunto(conjunto.oid),
      () => {
        if (montado.current) {
          setSucesso(msg('conjunto.excluido'))
        }
        return pesquisar()
      }
    )

  const colunaPadrao = (conjunto) => (
    <Checkbox
      checked={!!conjunto.padrao}
      disabled={!conjunto.editavel || ocupado}
      tooltip={conjunto.editavel ? msg('conjunto.padrao.help') : null}
      onChange={() => alternarPadrao(conjunto)}
    />
  )

  const colunaAcoes = (conjunto) => (
    <div className="filtros-salvos-acoes">
      <Button
        icon={aplicando[conjunto.oid] ? 'pi pi-spin pi-spinner' : 'pi pi-play'}
        className="p-button-success p-button-sm"
        tooltip={msg('conjunto.aplicar.help')}
        disabled={!!aplicando[conjunto.oid]}
        onClick={() => aplicar(conjunto)}
      />
      {conjunto.editavel && (
        <Button
          icon="pi pi-pencil"
          className="p-button-secondary p-button-sm"
          tooltip={msg('editar')}
          disabled={ocupado}
          onClick={() => setDialogo({ conjunto })}
        />
      )}
      {conjunto.editavel && (
        <Button
          icon="pi pi-trash"
          className="p-button-danger p-button-sm"
          tooltip={msg('conjunto.excluir')}
          disabled={ocupado}
          onClick={() => setAExcluir(conjunto)}
        />
      )}
    </div>
  )

  return (
    <React.Fragment>
      <Button
        label={msg('conjunto.salvar')}
        icon="pi pi-save"
        className="p-button-secondary"
        style={{ marginLeft: '.25em' }}
        onClick={novo}
      />
      <Button
        label={msg('conjunto.filtrosSalvos')}
        icon="pi pi-filter"
        className="p-button-secondary"
        style={{ marginLeft: '.25em' }}
        onClick={() => abrirPainel()}
      />
      {padraoAplicado && (
        // o nome do filtro em destaque dentro do texto do dicionario ('Filtro padrao: {0}')
        <span className="filtros-salvos-indicador">
          <i className="pi pi-filter" />
          {msg('conjunto.padrao.indicador').split('{0}')[0]}
          <strong>{padraoAplicado}</strong>
          {msg('conjunto.padrao.indicador').split('{0}')[1]}
        </span>
      )}
      {/* fora da barra de botoes da tela: painel e dialogos montados no body */}
      {ReactDOM.createPortal(
        <React.Fragment>
          <Sidebar
            visible={painelVisivel}
            position="right"
            onHide={() => setPainelVisivel(false)}
            className="filtros-salvos-painel"
            baseZIndex={999000}
          >
            <h3>{msg('conjunto.filtrosSalvos')}</h3>
            {/* as duas juntas: salvo ou excluido com a recarga da lista falhando, o sucesso continua valendo */}
            {sucesso && <Message severity="success" text={sucesso} className="filtros-salvos-mensagem" />}
            {erro && <Message severity="error" text={erro} className="filtros-salvos-mensagem" />}
            <DataTable
              value={conjuntos}
              lazy={true}
              paginator={true}
              first={pagina.first}
              rows={pagina.rows}
              totalRecords={total}
              onPage={(e) => {
                // a mensagem e da acao anterior: trocar de pagina nao salvou nem excluiu nada
                setSucesso(null)
                setPagina({ first: e.first, rows: e.rows })
                pesquisar(e.first, e.rows)
              }}
              loading={carregando}
              emptyMessage={msg('conjunto.vazio')}
              size="small"
              stripedRows
              responsiveLayout="scroll"
            >
              <Column field="descricao" header={msg('conjunto.nome')} />
              <Column header={msg('conjunto.visivelPara')} body={(c) => visivelPara(c, msg('conjunto.usuario'))} />
              <Column
                header={msg('conjunto.padrao')}
                body={colunaPadrao}
                style={{ width: '6em', textAlign: 'center' }}
              />
              <Column header={msg('conjunto.acoes')} body={colunaAcoes} style={{ width: '10em' }} />
            </DataTable>
            <div className="filtros-salvos-rodape">
              <Button
                label={msg('conjunto.fechar')}
                icon="pi pi-times"
                className="p-button-secondary"
                onClick={() => setPainelVisivel(false)}
              />
              <Button label={msg('conjunto.novo')} icon="pi pi-plus" className="p-button-success" onClick={novo} />
            </div>
          </Sidebar>
          {dialogo && (
            <AgendarRelatorioDialog
              conjunto={true}
              visible={true}
              onHide={() => setDialogo(null)}
              relatorio={relatorio}
              camposData={camposData}
              EditorFiltros={EditorFiltros}
              filtrosTela={dialogo.filtrosTela}
              agendamento={dialogo.conjunto}
              onSaved={aoSalvar}
            />
          )}
          <ConfirmDialog
            visible={!!aExcluir}
            content={aExcluir ? formatarTexto(msg('conjunto.excluir.confirmar'), aExcluir.descricao) : ''}
            confirm={() => excluir(aExcluir)}
            hideDialog={() => setAExcluir(null)}
          />
        </React.Fragment>,
        document.body
      )}
    </React.Fragment>
  )
}

export const FiltrosSalvosRelatorio = connect(null, mapDispatchToProps)(FiltrosSalvosRelatorioBase)
